// Shared "Email this report to client contacts" dialog — used by the Reports
// page (Documents + submitted/approved inspections) and directly from a
// completed inspection/report's own detail view.
import { useEffect, useMemo, useState } from "react";
import { Loader2, X, Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BRAND = "var(--brand-accent, #C75A1B)";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// target = { kind: "document" | "instance", id, title }
export function EmailReportDialog({ target, onClose }) {
    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [manualInput, setManualInput] = useState("");
    const [manualEmails, setManualEmails] = useState([]);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const recipientsUrl = target.kind === "document" ? `/documents/${target.id}/recipients` : `/forms/instances/${target.id}/recipients`;
    const sendUrl = target.kind === "document" ? `/documents/${target.id}/email` : `/forms/instances/${target.id}/email-pdf`;

    useEffect(() => {
        api.get(recipientsUrl).then((r) => setContacts(r.data || [])).catch(() => setContacts([])).finally(() => setLoadingContacts(false));
    }, [recipientsUrl]);

    const toggleContact = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const addManualEmail = () => {
        const email = manualInput.trim();
        if (!email) return;
        if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address."); return; }
        if (manualEmails.includes(email)) { setManualInput(""); return; }
        setManualEmails((m) => [...m, email]);
        setManualInput("");
    };
    const removeManualEmail = (email) => setManualEmails((m) => m.filter((e) => e !== email));

    const recipientEmails = useMemo(() => {
        const fromContacts = contacts.filter((c) => selected.has(c.id)).map((c) => c.email);
        return Array.from(new Set([...fromContacts, ...manualEmails]));
    }, [contacts, selected, manualEmails]);

    const send = async () => {
        if (recipientEmails.length === 0) { toast.error("Add at least one recipient."); return; }
        setSending(true);
        try {
            await api.post(sendUrl, { to: recipientEmails, message: message.trim() || undefined });
            toast.success(`Sent to ${recipientEmails.length} recipient${recipientEmails.length === 1 ? "" : "s"}.`);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send this report.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="email-report-dialog" onClick={onClose}>
            <div className="bg-white rounded-[14px] w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold flex-1 truncate">Email "{target.title}" to client</h3>
                    <button data-testid="email-report-close" onClick={onClose}><X className="w-4 h-4 text-[#9A968F]" /></button>
                </div>
                <div className="p-5 space-y-3.5 max-h-[65vh] overflow-y-auto">
                    <div>
                        <span className="text-[12px] font-semibold text-[#1F1F1F]">Client contacts</span>
                        {loadingContacts ? (
                            <div className="py-3 text-center"><Loader2 size={14} className="animate-spin text-[#9A968F] inline" /></div>
                        ) : contacts.length === 0 ? (
                            <p className="text-[12px] text-[#9A968F] mt-1">No contacts on file for this client yet — add an email manually below.</p>
                        ) : (
                            <div className="mt-1.5 max-h-32 overflow-y-auto border rounded-lg divide-y" style={{ borderColor: "#E5E0DA" }}>
                                {contacts.map((c) => (
                                    <label key={c.id} data-testid={`email-report-contact-${c.id}`} className="flex items-center gap-2 px-3 py-2 text-[12.5px] cursor-pointer hover:bg-[#F6F4F0]">
                                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleContact(c.id)} />
                                        <span className="font-medium text-[#1F1F1F]">{c.name}</span>
                                        <span className="text-[#9A968F] truncate">{c.email}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <span className="text-[12px] font-semibold text-[#1F1F1F]">Add an email manually</span>
                        <div className="flex gap-1.5 mt-1.5">
                            <input data-testid="email-report-manual-input" type="email" value={manualInput}
                                onChange={(e) => setManualInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManualEmail(); } }}
                                placeholder="name@example.com" className="flex-1 h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                            <button data-testid="email-report-manual-add" onClick={addManualEmail} type="button"
                                className="h-9 w-9 grid place-items-center rounded-lg border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}><Plus size={14} /></button>
                        </div>
                        {manualEmails.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {manualEmails.map((e) => (
                                    <span key={e} data-testid={`email-report-manual-chip-${e}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] bg-[#F0EDE8] text-[#1F1F1F]">
                                        {e} <button onClick={() => removeManualEmail(e)}><X size={10} /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <label className="block text-[12px] font-semibold text-[#1F1F1F]">Message <span className="text-[#9A968F] font-normal">(optional)</span>
                        <textarea data-testid="email-report-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                            placeholder="A short note to include in the email…" className="mt-1 w-full px-2.5 py-2 rounded-lg border text-[13px] resize-none" style={{ borderColor: "#E5E0DA" }} />
                    </label>
                </div>
                <div className="px-5 py-3.5 border-t flex items-center justify-between gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <span className="text-[11.5px] text-[#9A968F]">{recipientEmails.length} recipient{recipientEmails.length === 1 ? "" : "s"}</span>
                    <div className="flex gap-2">
                        <button data-testid="email-report-cancel" onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                        <button data-testid="email-report-send" onClick={send} disabled={sending}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: BRAND }}>
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
